/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import * as mobx from 'mobx';
import { observer } from 'mobx-react';
import Measure from 'react-measure';
import PropTypes from 'util/PropTypes';
import Card from './Card';
import StartingPointsCard from './StartingPointsCard';
import { A11YCollections, A11YDirection } from 'lib/@waca/a11y/src/js';
import CardViewFactory from './CardViewFactory';

import $ from 'jquery';
import './CardList.scss';

const BrowserUtils = require('gemini/lib/@waca/core-client/js/core-client/utils/BrowserUtils');

const LONGPRESSTIME = 500;
const SHORTPRESSTIME = 250;
const SILENT_GENERATION_SCROLL_TIMEOUT = 2000;
const SCROLL_DRAG_DISTANCE = 150;

const isFFOlderThanv60 = () => {
	const arr = [];
	return !arr.values;
};

class DnDHandler {
	constructor(className, dropHandler, onEnterHandler, onLeaveHandler, onDragEndHandler) {
		this.className = className;
		this.dropHandler = dropHandler;
		this.activateDropTarget = onEnterHandler;
		this.deactivateDropTarget = onLeaveHandler;
		this.endDrag = onDragEndHandler;
	}

	accepts = (/*dragObject*/) => {
		return true;
	}

	onDragEnd() {
		this.endDrag();
	}

	onDragEnter(dragObject, dropNode) {
		this.activateDropTarget(dragObject, dropNode);
	}

	onDragLeave(dragObject, dropNode) {
		this.deactivateDropTarget(dragObject, dropNode);
	}

	onDrop(dragObject, dropNode) {
		this.dropHandler(dragObject, dropNode);
	}
}

@observer
class CardList extends Component {

	constructor(props) {
		super(props);

		this.cardViewFactory = new CardViewFactory({
			model: props.cardListVM
		});

		this._rootRef = React.createRef();
		this._mobxDisposeEvents = [];
		this.listRef = null;

		this.scrollPosition = {
			top: 0,
			left: 0
		};

		this.nodeMap = new Map();

		this.pendingScrollTimeout = null;

		this.a11y = new A11YCollections({
			direction: [A11YDirection.UP_DOWN, A11YDirection.LEFT_RIGHT],
			handlers: {
				elements: (element) => this._getCardElements(element),
				container: (element) => this._getCardContainerElement(element)
			}
		});
		this._isReadyToDrag = false;
		this._isScrolling = false;
	}

	/**
	 * Calculates the necessary scroll top position required to keep the selected card within the viewport.
	 */
	calculateScrollTop(container, element) {
		if (this.shouldScrollToSelection && element && container) {
			this.shouldScrollToSelection = false;

			let scrollOffset = 0;
			const top = element.offsetTop - container.scrollTop;
			const margin = container.clientHeight / 10;

			if (top < 0) {
				scrollOffset = top - margin;
			} else if (top > container.clientHeight - element.clientHeight) {
				scrollOffset = top - container.clientHeight + margin + element.clientHeight;
			}

			if (scrollOffset) {
				const scrollTop = container.scrollTop + scrollOffset;

				return Math.max(0, scrollTop);
			}
		}
		return undefined;
	}

	scrollIfNecessary() {
		if (this.listRef && this.selectedCardRef) {
			const scrollTop = this.calculateScrollTop(this.listRef, this.selectedCardRef);

			if (scrollTop !== undefined) {
				$(this.listRef).animate({ scrollTop }, 500);
			}
		}
	}

	componentDidMount() {
		this._dndManager = this.props.dndManager;
		this.handleSilentThumbnailGeneration();

		this._mobxDisposeEvents.push(
			mobx.reaction(() => {
				return this.props.cardListVM.selection.items.slice();
			}, items => {
				if (items.length === 1) {
					// When only 1 item is selected, scroll to the selection.
					this.shouldScrollToSelection = true;
				}
			})
		);

		if (this._rootRef.current) {
			this._rootRef.current.querySelector('[tabindex]').focus();
		}
	}

	componentWillUnmount() {
		this.listRef = null;
		this.selectedCardRef = null;

		this.scrollPosition = null;

		this._mobxDisposeEvents.forEach(dispose => dispose());
		this._mobxDisposeEvents = [];
		clearTimeout(this._longPressTimer);
		this._longPressTimer = null;
	}


	render() {
		const cardArray = this.props.cardListVM.getCards();
		if( !cardArray ) {
			//when exploration is being closed, layout model & explore view model will get destroyed
			//cardListVM.getCards() accesses them
			return null;
		}
		const cards = this.renderItems();
		const startingPoints = this.renderStartingPoints();

		return (
			<div
				className="card-list-view" ref={this._rootRef}
				tabIndex="-1"
				onKeyDown={(e) => this.onKeyDown(e)}
			>
				<Measure
					innerRef={ref => this.listRef = ref}
					onResize={() => this.handleSilentThumbnailGeneration()}
				>
					{({ measureRef }) =>
						<div
							className="card-list-view__cards"
							onScroll={this._handleScroll.bind(this)}
							ref={measureRef}
						>
							{cards}
						</div>
					}
				</Measure>
				{startingPoints}
			</div>
		);
	}

	canDoSilentThumbnailGeneration() {
		const disable = BrowserUtils.isIE11() || ( BrowserUtils.isFirefox() && isFFOlderThanv60() );
		return !disable;
	}

	needsSilentGeneration(cardId) {
		if( !this.canDoSilentThumbnailGeneration() ) {
			return false;
		}

		const cardListVM = this.props.cardListVM;
		const card = cardListVM.getCard(cardId);
		const widgets = card && card.items || [];

		const isStaticCard = widgets.some(widget => !!widget.isStaticWidget);

		// if any of the widgets in the card is marked as static, we don't need silent generation
		if (isStaticCard) {
			return false;
		}

		const hasBlankWidget = cardListVM.containsBlankWidget(cardId);

		// if any of the widgets is blank (type === 'placeholder'), we don't need silent generation
		if (hasBlankWidget) {
			return false;
		}

		if(cardId === 'placeholder') {
			return false;
		}
		const node = this.nodeMap.get(cardId);
		if (node) {
			const $node = $(node);
			const $thumbnailItem = $node.find('.cardThumbnails__item');
			return $thumbnailItem.find('.visThumbnail').length === 0;
		} else {
			return false;
		}
	}

	isInViewport(cardId) {
		if (this.listRef && this.nodeMap.has(cardId)) {

			const offset = this.listRef.scrollTop;

			const card = this.nodeMap.get(cardId).parentElement;

			const coordinates = {
				viewport: {
					top: this.listRef.offsetTop,
					bottom: this.listRef.offsetTop + this.listRef.clientHeight
				},
				card: {
					top: card.offsetTop - offset,
					bottom: card.offsetTop + card.clientHeight - offset
				}
			};

			return coordinates.card.bottom >= coordinates.viewport.top
				&& coordinates.card.top <= coordinates.viewport.bottom;
		} else {
			return false;
		}
	}

	_handleScroll() {
		this._setScrollPositionForDragSupport();
		this.handleSilentThumbnailGeneration();
	}

	handleSilentThumbnailGeneration() {
		if (this.pendingScrollTimeout) {
			clearTimeout(this.pendingScrollTimeout);
		}
		this.props.cardListVM.resetLoadingCards();
		this.pendingScrollTimeout = setTimeout(this.onScroll, SILENT_GENERATION_SCROLL_TIMEOUT);
	}

	onScroll = () => {
		this.pendingScrollTimeout = null;

		const cardsInViewport = [];
		this.nodeMap.forEach((value, cardId)=>{
			if (this.isInViewport(cardId) && this.needsSilentGeneration(cardId)) {
				cardsInViewport.push(cardId);
			}
		});
		if (cardsInViewport.length > 0) {
			this.props.cardListVM.loadCards(cardsInViewport);
		}
	}

	renderItems() {
		let foundPlaceholder = false;
		const cardArray = this.props.cardListVM.getCards()
			.filter(item => {
				// We only want to reveal one placeholder since we know they will always occur in a connected sequence.
				if (item.id === 'placeholder') {
					if (foundPlaceholder) {
						// We've seen a placeholder before, filter out the rest.
						return false;
					}
					foundPlaceholder = true;
				}
				return true;
			});

		const count = cardArray.length;

		const types = {
			'placeholder': (...params) => this.renderPlaceholder(...params)
		};

		const cards = cardArray.map((card, index) => {
			const renderer = types[card.id];
			if (renderer) {
				return renderer(index);
			}
			return this.renderCard(card, count, index);
		});

		return cards;
	}

	renderCard(card, count, index) {
		const cardId = card.id;

		const showContextMenu = !this.props.cardListVM.isEditMode();
		const openContextMenu = this.props.cardListVM.shouldShowContextMenu([cardId]);
		const hasSelectedCards = this.props.cardListVM.hasSelectedCards();
		const selected = this.props.cardListVM.isCardSelected(card.id);
		const isNew = this.props.cardListVM.isCardNew(card.id);
		// The currently selected card or the last card should have tabindex="0".
		const tabIndex = (!hasSelectedCards && index === count - 1) || selected ? '0' : '-1';
		const cardView = this._getCardView(card);
		const cardType = card.items.length === 1 ? 'single' : 'compare';

		return (
			<div
				key={cardId}
				className="card-list-view__cards__container"
				data-entry-type="card"
				data-card-id={cardId}
				data-card-type={cardType}
				data-index={index+1}
				ref={node => {
					if (node && selected && this.props.cardListVM.selection.items.length === 1) {
						this.selectedCardRef = node;
						this.scrollIfNecessary();
					} else if (!node) {
						this.selectedCardRef = node;
					}
				}}
			>
				<div className="card-list-view__cards__container__ordinal">{index + 1}</div>
				<div
					className="card-list-view__cards__container__content"
					onMouseDown={(event) => this.onMouseDown(event, cardId)}
					onMouseUp={(event) => this.onMouseUp(event, cardId)}
					onTouchStart={(event) => this.onTouchStart(event, cardId)}
					onTouchEnd={(event) => this._onTouchEnd(event, cardId)}
					onTouchMove={this._onTouchMove.bind(this)}
					ref={(...params) => this._cacheNode(index, cardId, ...params)}
				>
					<Card
						id={cardId}
						title={card.getTitle()}
						view={cardView}
						tabIndex={tabIndex}
						isSelected={selected}
						isHidden={card.isHidden}
						hasFooter={showContextMenu}
						dndManager={this.props.dndManager}
						cacheNodeRef={() => {
							this.handleSilentThumbnailGeneration();
						}}
						onContextMenuToggle={showContextMenu && this.props.onContextMenuToggle.bind(this.props, cardId) || undefined}
						onContextMenuChange={showContextMenu && this.props.onContextMenuChange.bind(this.props) || undefined}
						contextMenuItems={openContextMenu && this.props.handlers.getContextMenuItems([card]) || undefined}
					/>
				</div>
				{isNew && <div className="card-list-view__cards__container--isNew" />}
			</div>
		);
	}

	renderStartingPoints() {
		const selected = this.props.cardListVM.isStartingPointsDisplayed();
		return (
			<div
				className="card-list-view__cards__container__content"
			>
				<StartingPointsCard
					isSelected={selected}
					onClick={() => this.onStartingPointsClick()}
				/>
			</div>
		);
	}

	renderPlaceholder(index) {
		const id = 'placeholder';

		return (
			<div
				className="card-list-view__cards__container card-list-view__cards__container--placeholder"
				data-entry-type="placeholder"
				data-card-id={id}
				data-index={index+1}
				key={index}
			>
				<div
					className="card-list-view__cards__container__content"
					ref={(...params) => this._cacheNode(index, id, ...params)}
				>
					<Card
						id="placeholder"
					/>
				</div>
			</div>
		);
	}

	// Event handlers

	onKeyDown(event) {
		switch (event.key) {
			case 'Backspace':
			case 'Delete':
				if (this.props.onDelete()) {
					event.preventDefault();
				}
				break;
			case ' ': // space
			case 'Enter':
				this._handleEnter(event);
				break;
			case 'c':
				if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
					/** ctrl + c */
					const cards = this.props.cardListVM.getSelectedCards();
					const copyAction = this.props.cardActions.getAction('copy');
					if(copyAction.isEnabled(cards)) {
						copyAction.action(cards);
					}

					event.preventDefault();
				}
				break;
			case 'a':
				if ((event.ctrlKey || event.metaKey) && !event.shiftKey) {
					/** ctrl + a */
					this.props.cardListVM.selectAll();

					event.preventDefault();
				}
				break;
			default:
				this.a11y.onKeyDown(event);
				break;
		}
	}

	onStartingPointsClick() {
		this.props.cardListVM.selectStartingPoints();
	}

	onCardClick(event, cardId) {
		this._setSelection(cardId, event);
	}

	startDrag($event, cardId) {
		if (!$event.currentTarget.childNodes.length) {
			return;
		}

		const element = $event.currentTarget.childNodes[0];

		const avatar = document.createElement('div');
		avatar.classList.add('card-list-view__avatar');

		let cards;
		if (this.props.cardListVM.isCardSelected(cardId)) {
			const cardElements = $(this.listRef).find('.card-list-view__cards__container .card.selected');
			const topAvatar = cardElements[cardElements.length - 1].cloneNode(true);

			cards = [];
			for (let i = 0; i < cardElements.length; i++) {
				cards.push(cardElements.eq(i).closest('.card-list-view__cards__container').data('card-id'));
			}

			const count = Math.min(cards.length, 3);
			let cursor = avatar;

			for (let i = 0; i < count; i++) {
				const holder = document.createElement('div');
				holder.classList.add('card-list-view__avatar__container');

				const relative = document.createElement('div');
				relative.classList.add('card-list-view__avatar__relative');

				let node;
				if (i < count - 1) {
					node = document.createElement('div');
					node.classList.add('card');
					node.classList.add('selected');
				} else {
					node = topAvatar;
				}

				relative.appendChild(node);
				holder.appendChild(relative);
				cursor.appendChild(holder);

				cursor = holder;
			}
		} else {
			cards = [cardId];

			avatar.appendChild(element.cloneNode(true));
		}

		// not too happy to do this ↓ , perhaps we need to augment DNDManager to be able to set these properly
		avatar.style.position = 'absolute'; // dnd expects avatar position:absolute
		avatar.style.zIndex = '10000';

		this._dndManager.startDrag({
			event: $event,
			avatar,
			type: 'card',
			data: cards,
			avatarXOffset: -(element.offsetWidth / 2),
			avatarYOffset: -(element.offsetHeight / 2),
			moveXThreshold: 20,
			moveYThreshold: 20,
			callerCallbacks: {
				onDragStart: () => {
					this.props.cardListVM.beginDrag(cards);
				},
				onMove: (event, options) => {
					this._handleScrollDrag($event, options);
				},
				onDragDone: (event, options) => {
					this.props.cardListVM.endDrag();

					$(options.dragObject.avatar).hide();

					// Have to also reset the flag here, because after dragging a card,
					// the onMouseUp or onTouchEnd callback of the dragged card will not get called
					this._isReadyToDrag = false;
					this._isLongPress = false;
				}
			}
		});
	}

	_handleScrollDrag(movedCardEvent, options) {
		const dragObject = options.dragObject;
		const scrollableContainer = this.listRef;
		const boundingRect = scrollableContainer.getBoundingClientRect();
		const rect = {
			top: boundingRect.top,
			bottom: boundingRect.bottom
		};
		const position = dragObject.position;
		const distanceBottom = rect.bottom - position.y;
		const distanceTop = position.y - rect.top;
		// Determine whether drag direction is to the top or bottom
		// isScrollingBottom when the distance of the dragged card to the bottom of the container
		// is between 0 and SCROLL_DRAG_DISTANCE (i.e. scroll distance buffer for scrolling 150)
		let isScrollingBottom = distanceBottom > 0 && distanceBottom < SCROLL_DRAG_DISTANCE;
		// isScrollingTop when the distance of the dragged card to the top of the container
		// is between 0 and SCROLL_DRAG_DISTANCE (i.e. scroll distance buffer for scrolling 150)
		let isScrollingTop = distanceTop > 0 && distanceTop < SCROLL_DRAG_DISTANCE;
		let distanceY = 0;
		// If both isScrollingTop and isScrollingBottom are true (almost never happens), choose scrolling direction based on the larger distance
		if (isScrollingTop && isScrollingBottom) {
			isScrollingTop = distanceTop > distanceBottom;
			isScrollingBottom = !isScrollingTop;
		}
		if (isScrollingBottom) {
			distanceY = SCROLL_DRAG_DISTANCE - distanceBottom;
		} else if (isScrollingTop) {
			distanceY = distanceTop - SCROLL_DRAG_DISTANCE;
		}
		scrollableContainer.scrollTop += distanceY;

		const deltaScrollTop = this.scrollPosition.top - scrollableContainer.scrollTop;
		const deltaScrollLeft = this.scrollPosition.left - scrollableContainer.scrollLeft;

		// Add change in scrollTop and scrollLeft to cached clientRects of moved card in dndManager
		if (movedCardEvent.currentTarget) {
			const movedCardCandidateDropTarget = this._dndManager.getCandidateDropTargetFromNode(movedCardEvent.currentTarget);
			if (movedCardCandidateDropTarget) {
				movedCardCandidateDropTarget.clientRects.forEach((clientRect) => {
					clientRect.top += deltaScrollTop;
					clientRect.left += deltaScrollLeft;
				});
			}
		}

		// Add change in scrollTop and scrollLeft to cached clientRects of children of cardList
		this._dndManager.scrollChildrenCandidateDropTargets(scrollableContainer, deltaScrollTop, deltaScrollLeft);

		this._setScrollPositionForDragSupport();
	}

	_setScrollPositionForDragSupport() {
		if(this.listRef) {
			this.scrollPosition = {
				top: this.listRef.scrollTop,
				left: this.listRef.scrollLeft
			};
		}
	}

	onMouseDown(event, cardId, isMobile = false) {
		if ($(event.target).closest('.ba-common-context-menu').length > 0) {
			return;
		}
		event.persist();
		// ↓ caching event.currentTarget which used by DnDManager
		const $event = $.Event(event.type, event);
		const callback = (e) => {
			const isCardSelected = this.props.cardListVM.isCardSelected(cardId);
			this._isLongPress = true;
			if (isMobile) {
				if (!isCardSelected && !this.props.cardListVM.isDragging()) {
					this.onCardClick(event, cardId);
				}
			} else { // ↓ dont drag on context menu actions
				if (!$(e.target).hasClass('ba-common-menu__item') && $(e.target).parents('.ba-common-menu__item').length === 0) {
					this.startDrag(e, cardId);
				}
			}
		};
		this._longPressTimer = setTimeout(callback, isMobile ? LONGPRESSTIME : SHORTPRESSTIME, $event);
		if (isMobile) {
			this._mobileStartDragTimer = setTimeout(($event) => {
				this.startDrag($event, cardId);
				this._isReadyToDrag = true;
			}, SHORTPRESSTIME, $event);
		}
	}

	_onTouchMove(){
		if(this._isReadyToDrag || this._isScrolling) {
			// If already in dragging status or scrolling status, do nothing
			return;
		} else {
			// Not ready to drag, then do scrolling
			// set scrolling status to true
			this._isScrolling = true;
			if(this._mobileStartDragTimer){
				clearTimeout(this._mobileStartDragTimer);
				this._mobileStartDragTimer = null;
			}
			if(this._longPressTimer){
				clearTimeout(this._longPressTimer);
				this._longPressTimer = null;
			}
		}
	}

	onMouseUp(event, cardId) {
		if (this._isLongPress) {
			this._isLongPress = false;
		} else {
			this.onCardClick(event, cardId);
		}
		clearTimeout(this._longPressTimer);
		this._longPressTimer = null;
		// Prevents the mouse up event when touch event happens
		event.preventDefault();
	}

	onTouchStart(event, cardId) {
		this.onMouseDown(event, cardId, true);
	}

	// Private

	_onTouchEnd(event, cardId) {
		if(this._isScrolling){
			this._isScrolling = false;
		} else {
			if(this._isReadyToDrag){
				this._isReadyToDrag = false;
			}
			if (this._isLongPress) {
				this._isLongPress = false;
			} else {
				// make sure event is NOT coming from the context menu, but from click on the actual card
				if (!$(event.target).parents('.ba-common-menu__item').length) {
					this.onCardClick(event, cardId);
				}
			}
			clearTimeout(this._longPressTimer);
			this._longPressTimer = null;
			clearTimeout(this._mobileStartDragTimer);
			this._mobileStartDragTimer = null;
		}
		// Prevents the mouse up event when touch event happens
		if(event.cancelable !== false){
			event.preventDefault();
		}
	}

	_isCardElement(element) {
		return this._getCardContainerElement(element).data('entry-type') === 'card';
	}

	_getCardView(card) {
		return this.cardViewFactory.makeCardView(card);
	}

	_getCardContainerElement(element) {
		return $(element).closest('.card-list-view__cards__container');
	}

	_isStartingPointsElement(element) {
		return this._getCardContainerElement(element).data('entry-type') === 'starting-points';
	}

	_getCardElements(element) {
		return $(element)
			.closest('.card-list-view')
			.find('.card-list-view__cards__container .card');
	}

	_getCardId(element) {
		const container = this._getCardContainerElement(element);
		return container.data('card-id');
	}

	_handleEnter(event) {
		if(this._isCardElement(event.target)) {
			const id = this._getCardId(event.target);
			if(id) {
				this._setSelection(id, event);

				event.preventDefault();
			}
		} else if(this._isStartingPointsElement(event.target)) {
			this.props.cardListVM.selectStartingPoints();

			event.preventDefault();
		}
	}

	_setSelection(id, event) {
		const multiselect = event.ctrlKey || event.metaKey || this._isLongPress;
		const rangeselect = event.shiftKey && !multiselect;

		this.props.cardListVM.selectCard(id, {
			multiselect,
			rangeselect
		});
	}

	_cacheNode(index, cardId, node) {
		const previousNode = this.nodeMap.get(cardId);

		if (previousNode) {
			this.nodeMap.delete(cardId);

			this.props.dndManager && this.props.dndManager.removeDropTarget(previousNode);
		}

		if (node) {
			this.nodeMap.set(cardId, node);

			this.props.dndManager && this.props.dndManager.addDropTarget(
				node,
				new DnDHandler(
					'dndHover',
					(...params) => this._handleOnDrop(index, ...params),
					(...params) => this._handleOnDragEnter(cardId, ...params),
					(...params) => this._handleOnDragLeave(index, ...params),
					(...params) => this._handleOnDragEnd(index, ...params),
				)
			);
		}
	}

	_handleOnDrop() { return; } // leaving definition for potential future use(widget to explore drop)

	_handleOnDragEnter(cardId) {
		this.props.cardListVM.movePlaceHolderToCard(cardId);
	}

	_handleOnDragLeave() { return; } // leaving definition for potential future use(widget to explore drop)

	_handleOnDragEnd() { return; } // end drag is handled by the callback onDragDone in startDrag in this class

}

CardList.propTypes = {
	handlers: PropTypes.object,
	cardListVM: PropTypes.object.isRequired,
	cardActions: PropTypes.object.isRequired,
	thumbnails: PropTypes.object,
	onContextMenuToggle: PropTypes.func,
	onContextMenuChange: PropTypes.func,
	onDelete: PropTypes.func,
	dndManager: PropTypes.object.isRequired,
	dndThresholdPx: PropTypes.number
};

CardList.defaultProps = {
	onContextMenuToggle: function() {},
	onContextMenuChange: function() {},
	dndThresholdPx: 10
};

export default CardList;



// WEBPACK FOOTER //
// ./src/js/components/cards/CardList.js